import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

export default function ClientView() {
  const { id } = useParams(); // user id from route

  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);

  // ===== Fetch Client Details =====
  const getClientDetails = async () => {
    try {
      const [resUser, resInvestment] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/${id}`),
        axios.get(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/investment/user/${id}`)
      ]);

      if (resUser.status === 200 && resInvestment.status === 200) {
        setUser({
          ...resUser.data.data,
          ...resInvestment.data.data,
        });
      }
    } catch (error) {
      console.error("Failed to load client details", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getClientDetails();
  }, [id]);

  if (loading) {
    return <div className="p-4">Loading client details...</div>;
  }

  return (
    <>
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content">
            {/* Breadcrumb and Header Section with Card Shadow */}
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "20px",
              }}
            >
              
              <nav
                aria-label="breadcrumb"
                style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}
              >
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link> /
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/user">Users</Link> /
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Client View Details
                  </li>
                </ol>
              </nav>
              <div className="content-page-header content-page-headersplit">
                <h5>Client View Details</h5>
                <div className="list-btn d-flex gap-2">
                  {user.ucc && user.pan_number && (
                    <>
                      <Link
                        to="/user/aof-upload"
                        state={{ clientCode: user.ucc, pan: user.pan_number }}
                        className="btn btn-primary btn-sm"
                      >
                        <i className="fas fa-file-signature me-2"></i>
                        AOF Upload
                      </Link>
                      <Link
                        to="/user/cheque-upload"
                        state={{
                          clientCode: user.ucc,
                          pan: user.pan_number,
                          accountHolderType: user.residential_status === "NRI" ? "NRI" : "N",
                        }}
                        className="btn btn-outline-primary btn-sm"
                      >
                        <i className="fas fa-money-check-alt me-2"></i>
                        Cheque Upload
                      </Link>
                    </>
                  )}
                  <Link to="/user" className="btn btn-cancel">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back
                  </Link>
                </div>
              </div>
            </div>

            {/* Profile Header Section with Card Shadow */}
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                }}
              >
                {user.photo ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_BASE_URL_FILES}${user.photo}`}
                    alt="Profile"
                    style={{
                      width: "90px",
                      height: "90px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid #e6f6f5",
                    }}
                    onError={(e) => {
                      e.currentTarget.src = "/assets/img/user.png";
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "90px",
                      height: "90px",
                      borderRadius: "50%",
                      background: "#e6f6f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#0bc1ab",
                      fontSize: "40px",
                      border: "3px solid #e6f6f5",
                    }}
                  >
                    <i className="fas fa-user"></i>
                  </div>
                )}
                <div>
                  <h3 style={{ margin: 0, fontWeight: 600, color: "#0D233E", fontSize: "24px" }}>
                    {user.first_name} {user.last_name}
                  </h3>
                  <span style={{ color: "#6b7280", fontSize: "14px" }}>{user.email}</span>
                </div>
              </div>
            </div>

            {/* Personal Details Section with Card Shadow */}
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "20px",
              }}
            >
              <h4
                style={{
                  marginBottom: "20px",
                  borderBottom: "2px solid #e6f6f5",
                  paddingBottom: "12px",
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#006e82",
                }}
              >
                Personal Details
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: "20px",
                }}
              >
                <Info label="Phone" value={`${user.country_code || ""} ${user.phone || "-"}`} />
                <Info label="Date of Birth" value={user.date_of_birth?.split("T")[0] || "-"} />
                <Info label="Gender" value={user.gender || "-"} />
                <Info label="PAN Number" value={user.pan_number || "-"} />
                <Info label="Address Line 1" value={user.address_line1 || "-"} />
                <Info label="Address Line 2" value={user.address_line2 || "-"} />
                <Info label="Country" value={user.country || "-"} />
                <Info label="State" value={user.state || "-"} />
                <Info label="City" value={user.city || "-"} />
                <Info label="Pin Code" value={user.pin_code || "-"} />
                <Info label="Residential Status" value={user.residential_status || "-"} />
                <Info label="UCC" value={user.ucc || "-"} />
              </div>
            </div>

            {/* Bank Details Section with Card Shadow */}
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "20px",
              }}
            >
              <h4
                style={{
                  marginBottom: "20px",
                  borderBottom: "2px solid #e6f6f5",
                  paddingBottom: "12px",
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#006e82",
                }}
              >
                Bank Details
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: "20px",
                }}
              >
                <Info label="Bank Name" value={user.bank_name || "-"} />
                <Info label="Branch Name" value={user.branch_name || "-"} />
                <Info label="Account Number" value={user.account_number || "-"} />
                <Info label="Account Type" value={user.account_type || "-"} />
                <Info label="IFSC Code" value={user.ifsc_code || "-"} />
                <Info label="Nominee Name" value={user.nominee_name || "-"} />
                <Info label="Nominee Relation" value={user.nominee_relationship || "-"} />
                <Info label="Nominee DOB" value={user.nominee_dob?.split("T")[0] || "-"} />
                <Info label="Guardian Details" value={user.guardian_details || "-"} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .info-item {
          display: flex;
          flex-direction: column;
        }

        .info-item label {
          color: #0D233E;
          font-size: 14px;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .info-item div {
          border: 1px solid #E3E3E3;
          font-weight: 400;
          color: #0D233E;
          min-height: 46px;
          padding: 12px 15px;
          border-radius: 8px;
          background: #f9fafb;
          font-size: 14px;
          line-height: 1.5;
          display: flex;
          align-items: center;
        }
      `}</style>
    </>
  );
}



/* ===== Reusable Info Component ===== */
function Info({ label, value }) {
  return (
    <div className="info-item">
      <label>{label}</label>
      <div>{value}</div>
    </div>
  );
}
